import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  LinearProgress,
  Divider,
  Paper,
  Stack,
  Tooltip,
  IconButton,
} from "@mui/material";
import {
  GpsFixed,
  Warning,
  People,
  AttachMoney,
  FlashOn,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import transformOWASPData from "../DASTInsightsPages/charts/transformOWASPData";
import InfoIcon from "@mui/icons-material/Info";
import WarningIcon from "@mui/icons-material/Warning";
import {
  Bar,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";
// import ProgressBarMUI from "../DASTInsightsPages/charts/ProgressBarMUI";

const tooltipTexts = {
  "Vulnerability Count":
    "Difference in total unresolved vulnerabilities between Run 1 and Run 2.",
  "Vulnerability Distribution by Severity":
    "Change in severity breakdown of unresolved vulnerabilities between scans.",
  "Vulnerability Density":
    "Change in open vulnerability density between scans.",
  "Overall Security Posture":
    "Individual metric showing improvement or decline in security score between runs.",
  "Business Impact Score":
    "Difference in potential business risk between Run 1 and Run 2 results.",
  "Technical Impact Score":
    "Difference in potential technical risk between Run 1 and Run 2 results.",
  ROI: "Change in ROI score reflecting remediation cost-effectiveness between runs.",
  "Compliance Requirement Met": "Change in compliance coverage between scans.",
  "Overall Risk Rating":
    "Difference in overall numeric risk rating between scans.",
  "Closed Vulnerabilities by Severity":
    "Individual metric showing number of vulnerabilities fixed from Run 1, grouped by severity.",
  "Remediation Effectiveness":
    "Percentage of identified vulnerabilities that have been successfully resolved between runs.",
  "Age of Vulnerability": "Change in average vulnerability age between scans.",
  "Average Time to Remediation":
    "Difference in average resolution time between runs.",
  "New Vulnerabilities":
    "Individual metric showing count of newly introduced vulnerabilities after Run 1.",
  "OWASP Top 10":
    "Indicates whether all OWASP Top 10 vulnerability categories were identified and addressed. Useful for assessing security coverage.",
};

const OwaspComparisonChart = ({ run1Data, run2Data, title }) => {
  // Create a comprehensive list of all categories from both runs
  const allCategories = new Map();

  // Add categories from run1
  run1Data.forEach((item) => {
    allCategories.set(item.label, {
      label: item.label,
      color: item.color,
      run1Value: item.value,
      run2Value: 0,
    });
  });

  // Add/update categories from run2
  run2Data.forEach((item) => {
    if (allCategories.has(item.label)) {
      allCategories.get(item.label).run2Value = item.value;
    } else {
      allCategories.set(item.label, {
        label: item.label,
        color: item.color,
        run1Value: 0,
        run2Value: item.value,
      });
    }
  });

  // Convert to array and filter out categories with 0 vulnerabilities in both runs
  const filteredData = Array.from(allCategories.values())
    .filter((item) => item.run1Value > 0 || item.run2Value > 0)
    .sort((a, b) => b.run1Value + b.run2Value - (a.run1Value + a.run2Value));

  const maxValue = Math.max(
    ...filteredData.map((item) => Math.max(item.run1Value, item.run2Value))
  );

  return (
    <Box sx={{ height: "100%" }}>
      <CardContent>
        {title && (
          <Typography variant="h6" gutterBottom>
            {title}
          </Typography>
        )}
        <Stack spacing={3}>
          {filteredData.map((item, index) => {
            const reduction =
              item.run1Value > 0
                ? Math.round(
                    ((item.run1Value - item.run2Value) / item.run1Value) * 100
                  )
                : 0;

            return (
              <Box key={index}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={1}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "0.75rem", fontWeight: 500 }}
                  >
                    {item.label}
                  </Typography>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Typography variant="caption">
                      Run 1: {item.run1Value}
                    </Typography>
                    <Typography variant="caption">
                      Run 2: {item.run2Value}
                    </Typography>
                    {reduction >= 0 ? (
                      <Typography
                        variant="caption"
                        color="success.main"
                        fontWeight="bold"
                      >
                        -{reduction}%
                      </Typography>
                    ) : (
                      <Typography
                        variant="caption"
                        color="error.main"
                        fontWeight="bold"
                      >
                        +{Math.abs(reduction)}%
                      </Typography>
                    )}
                  </Stack>
                </Stack>
                <Stack direction="row" spacing={1}>
                  <Box sx={{ flex: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 2,
                      }}
                    >
                      <LinearProgress
                        variant="determinate"
                        value={
                          maxValue > 0 ? (item.run1Value / maxValue) * 100 : 0
                        }
                        sx={{
                          flex: 1,
                          height: 6,
                          bgcolor: "grey.200",
                          borderRadius: 3,
                          "& .MuiLinearProgress-bar": {
                            bgcolor: item.color,
                            opacity: 0.6,
                            borderRadius: 3,
                          },
                        }}
                      />
                      <Typography sx={{ fontSize: "18px", fontWeight: 600 }}>
                        {item.run1Value}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Run 1
                    </Typography>
                  </Box>

                  <Box sx={{ flex: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 2,
                      }}
                    >
                      <LinearProgress
                        variant="determinate"
                        value={
                          maxValue > 0 ? (item.run2Value / maxValue) * 100 : 0
                        }
                        sx={{
                          flex: 1,
                          height: 6,
                          bgcolor: "grey.200",
                          borderRadius: 3,
                          "& .MuiLinearProgress-bar": {
                            bgcolor: item.color,
                            borderRadius: 3,
                          },
                        }}
                      />
                      <Typography sx={{ fontSize: "18px", fontWeight: 600 }}>
                        {item.run2Value}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Run 2
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            );
          })}
        </Stack>

        {/* Summary Section */}
        <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: "divider" }}>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              Total Run 1:{" "}
              {filteredData.reduce((sum, item) => sum + item.run1Value, 0)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Run 2:{" "}
              {filteredData.reduce((sum, item) => sum + item.run2Value, 0)}
            </Typography>
            <Typography variant="body2" color="success.main" fontWeight="bold">
              Overall Reduction:{" "}
              {(() => {
                const totalRun1 = filteredData.reduce(
                  (sum, item) => sum + item.run1Value,
                  0
                );
                const totalRun2 = filteredData.reduce(
                  (sum, item) => sum + item.run2Value,
                  0
                );
                return totalRun1 > 0
                  ? Math.round((1 - totalRun2 / totalRun1) * 100)
                  : 0;
              })()}
              %
            </Typography>
          </Stack>
        </Box>
      </CardContent>
    </Box>
  );
};

// Function to get dynamic description based on score
const getSecurityPostureDescription = (score) => {
  if (score >= 0.1 && score <= 3.9) {
    return "Critical Posture";
  } else if (score >= 4.0 && score <= 6.9) {
    return "High Posture";
  } else if (score >= 7.0 && score <= 8.9) {
    return "Medium Posture";
  } else if (score >= 9.0 && score <= 10.0) {
    return "Low Posture";
  } else {
    return "Unknown";
  }
};

// ===== Styled components =====
const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
  },
  [theme.breakpoints.down("sm")]: {
    borderRadius: 12,
    "&:hover": {
      transform: "none", // Disable hover effects on mobile
    },
  },
}));

// function getGradientColors(color) {
//   const gradients = {
//     blue: "#1976d2, #42a5f5",
//     green: "#388e3c, #66bb6a",
//     red: "#d32f2f, #f44336",
//     orange: "#f57c00, #ff9800",
//     purple: "#7b1fa2, #ba68c8",
//   };
//   return gradients[color] || gradients.blue;
// }

const MetricCard = styled(Card)(({ color, theme }) => ({
  borderRadius: 16,
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "scale(1.02)",
    boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
  },
  [theme.breakpoints.down("sm")]: {
    borderRadius: 12,
    "&:hover": {
      transform: "none",
    },
  },
}));

const ComplianceCard = ({ title, status, tooltipText = "" }) => (
  <Paper
    sx={{
      p: 2,
      textAlign: "center",
      backgroundColor: "#f8f9fa",
      height: "82px",
      borderRadius: "8px",
      border: "1px solid #5391F7",
      position: "relative", // Added for tooltip positioning
    }}
  >
    {/* Info Icon with Tooltip */}
    {tooltipText && (
      <Tooltip
        title={tooltipText}
        placement="top"
        arrow
        componentsProps={{
          tooltip: {
            sx: {
              maxWidth: 300,
              fontSize: "14px",
              lineHeight: 1.4,
            },
          },
        }}
      >
        <IconButton
          size="small"
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            padding: "4px",
            color: "#5391F7",
            "&:hover": {
              backgroundColor: "rgba(83, 145, 247, 0.1)",
            },
          }}
          aria-label="Info"
        >
          <InfoIcon fontSize="small" sx={{ color: "grey.500" }} />
        </IconButton>
      </Tooltip>
    )}

    {/* Card Content */}
    <Typography sx={{ fontWeight: 600, mb: 1, fontSize: "18px" }}>
      {title}
    </Typography>
    <Typography
      sx={{
        fontWeight: 700,
        fontSize: "27px",
      }}
    >
      {status}
    </Typography>
  </Paper>
);

// Updated DonutChart component with bigger sizing
const DonutChart = ({ data, title, size, tooltipText = "" }) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  let cumulativePercentage = 0;

  // Responsive size calculation
  const responsiveSize = size || 180; // Default bigger size
  const radius = responsiveSize * 0.4; // Dynamic radius based on size
  const strokeWidth = responsiveSize * 0.15; // Dynamic stroke width
  const circumference = 2 * Math.PI * radius;

  return (
    <StyledCard
      sx={{
        borderRadius: 2,
        border: "1px solid #5391F7",
        backgroundColor: "#f8f9fa",
        height: "100%",
        position: "relative", // Added for tooltip positioning
      }}
    >
      {/* Info Icon with Tooltip */}
      {tooltipText && (
        <Tooltip
          title={tooltipText}
          placement="top"
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                maxWidth: 300,
                fontSize: "14px",
                lineHeight: 1.4,
              },
            },
          }}
        >
          <IconButton
            size="small"
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              padding: "4px",
              color: "#5391F7",
              "&:hover": {
                backgroundColor: "rgba(83, 145, 247, 0.1)",
              },
              zIndex: 10,
            }}
            aria-label="Info"
          >
            <InfoIcon fontSize="small" sx={{ color: "grey.500" }} />
          </IconButton>
        </Tooltip>
      )}

      <CardContent
        sx={{ height: "100%", display: "flex", flexDirection: "column" }}
      >
        <Typography
          variant="h6"
          gutterBottom
          fontWeight="600"
          sx={{
            fontSize: { xs: "16px", sm: "18px", md: "20px" },
            mb: { xs: 1, md: 2 },
          }}
        >
          {title}
        </Typography>

        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: 2, md: 3 }, // Increased gap for bigger donut
            flex: 1,
          }}
        >
          <DonutContainer>
            <svg
              width={responsiveSize}
              height={responsiveSize}
              viewBox={`0 0 ${responsiveSize} ${responsiveSize}`}
              style={{
                maxWidth: "100%",
                minWidth: { xs: "180px", md: "220px" }, // Minimum size constraints
              }}
            >
              <circle
                cx={responsiveSize / 2}
                cy={responsiveSize / 2}
                r={radius}
                fill="none"
                stroke="#f5f5f5"
                strokeWidth={strokeWidth}
              />
              {data.map((item, index) => {
                const percentage = (item.value / total) * 100;
                const strokeDasharray = `${
                  (percentage / 100) * circumference
                } ${circumference}`;
                const strokeDashoffset =
                  -(cumulativePercentage / 100) * circumference;
                cumulativePercentage += percentage;

                return (
                  <circle
                    key={index}
                    cx={responsiveSize / 2}
                    cy={responsiveSize / 2}
                    r={radius}
                    fill="none"
                    stroke={item.color}
                    strokeWidth={strokeWidth}
                    strokeDasharray={strokeDasharray}
                    strokeDashoffset={strokeDashoffset}
                    style={{
                      transition: "all 0.3s ease",
                      transform: "rotate(-90deg)",
                      transformOrigin: `${responsiveSize / 2}px ${
                        responsiveSize / 2
                      }px`,
                    }}
                  />
                );
              })}
            </svg>
            <DonutCenter>
              <Typography
                variant="h4"
                fontWeight="bold"
                sx={{ fontSize: { xs: "24px", sm: "28px", md: "36px" } }} // Increased font size
              >
                {total}
              </Typography>
            </DonutCenter>
          </DonutContainer>

          <Box
            sx={{
              width: { xs: "100%", md: "auto" },
              minWidth: { md: "150px" },
            }}
          >
            {data.map((item, index) => (
              <Box
                key={index}
                display="flex"
                alignItems="center"
                mb={1.5} // Increased spacing for better proportion
                sx={{
                  justifyContent: { xs: "space-between", md: "flex-start" },
                }}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <Box
                    width={{ xs: 14, md: 16 }} // Slightly bigger legend dots
                    height={{ xs: 14, md: 16 }}
                    borderRadius="50%"
                    bgcolor={item.color}
                  />
                  <Typography
                    variant="body2"
                    sx={{ fontSize: { xs: "13px", sm: "15px" } }} // Increased font size
                  >
                    {item.label}
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  fontWeight="600"
                  sx={{
                    ml: { xs: 0, md: 1 },
                    fontSize: { xs: "13px", sm: "15px" }, // Increased font size
                  }}
                >
                  {item.value}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </CardContent>
    </StyledCard>
  );
};

// Bar Chart Component with responsive design
const BarChart = ({ data, title, tooltipText = "" }) => {
  const maxValue = Math.max(...data.map((item) => item.value));

  return (
    <StyledCard
      sx={{
        borderRadius: 2,
        border: "1px solid #5391F7",
        backgroundColor: "#f8f9fa",
        height: "100%",
        position: "relative", // Added for tooltip positioning
      }}
    >
      {/* Info Icon with Tooltip */}
      {tooltipText && (
        <Tooltip
          title={tooltipText}
          placement="top"
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                maxWidth: 300,
                fontSize: "14px",
                lineHeight: 1.4,
              },
            },
          }}
        >
          <IconButton
            size="small"
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              padding: "4px",
              color: "#5391F7",
              "&:hover": {
                backgroundColor: "rgba(83, 145, 247, 0.1)",
              },
              zIndex: 10,
            }}
            aria-label="Info"
          >
            <InfoIcon fontSize="small" sx={{ color: "grey.500" }} />
          </IconButton>
        </Tooltip>
      )}

      <CardContent
        sx={{ height: "100%", display: "flex", flexDirection: "column" }}
      >
        <Typography
          variant="h6"
          gutterBottom
          fontWeight="600"
          sx={{
            fontSize: { xs: "16px", sm: "18px", md: "20px" },
            mb: { xs: 1, md: 2 },
          }}
        >
          {title}
        </Typography>
        <Stack
          spacing={{ xs: 1.5, md: 2 }}
          sx={{ flex: 1, justifyContent: "center" }}
        >
          {data.map((item, index) => (
            <Box key={index}>
              <Box display="flex" justifyContent="space-between" mb={0.5}>
                <Typography
                  variant="body2"
                  sx={{ fontSize: { xs: "12px", sm: "14px" } }}
                >
                  {item.label}
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight="600"
                  sx={{ fontSize: { xs: "12px", sm: "14px" } }}
                >
                  {item.value}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={(item.value / maxValue) * 100}
                sx={{
                  height: { xs: 6, md: 8 },
                  borderRadius: { xs: 3, md: 4 },
                  backgroundColor: "#f5f5f5",
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: item.color,
                    borderRadius: { xs: 3, md: 4 },
                  },
                }}
              />
            </Box>
          ))}
        </Stack>
      </CardContent>
    </StyledCard>
  );
};

const DonutContainer = styled(Box)({
  position: "relative",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
});

const DonutCenter = styled(Box)({
  position: "absolute",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
});

// Responsive Run Comparison Card Component
const RunComparisonCard = ({
  title,
  run1Value,
  run2Value,
  run1Label,
  run2Label,
  difference,
  improvement,
  icon: Icon,
  tooltipText = "Title",
}) => {
  return (
    <StyledCard
      sx={{
        borderRadius: 2,
        border: "1px solid #5391F7",
        height: "100%",
        minHeight: { xs: "200px", md: "250px" }, // Add minimum height
        display: "flex",
        flexDirection: "column",
      }}
    >
      <CardContent
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          p: { xs: 2, md: 3 }, // Responsive padding
          "&:last-child": { pb: { xs: 2, md: 3 } }, // Fix Material-UI padding issue
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={{ xs: 2, md: 3 }}
        >
          <Typography
            variant="h6"
            fontWeight="600"
            sx={{
              fontSize: { xs: "14px", sm: "16px", md: "18px" },
              lineHeight: 1.2,
            }}
          >
            {title}
          </Typography>

          <Tooltip
            title={tooltipText}
            placement="top"
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  maxWidth: 300, // Limit tooltip width for better readability
                  fontSize: "14px",
                  lineHeight: 1.4,
                },
              },
            }}
          >
            <IconButton
              size="small"
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                padding: "4px",
                color: "#5391F7",
                "&:hover": {
                  backgroundColor: "rgba(83, 145, 247, 0.1)",
                },
              }}
              aria-label="Info"
            >
              <InfoIcon fontSize="small" sx={{ color: "grey.500" }} />
            </IconButton>
          </Tooltip>
        </Box>

        <Grid
          container
          spacing={{ xs: 2, md: 3 }}
          sx={{ flex: 1, mb: { xs: 1.5, md: 2 } }}
        >
          <Grid item xs={6}>
            <Paper
              sx={{
                p: { xs: 1.5, md: 2 },
                bgcolor: "#ffebee",
                border: "2px solid #ffcdd2",
                borderRadius: { xs: 1, md: 2 },
                textAlign: "center",
                height: { xs: "80px", md: "100px" }, // Fixed height
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <Typography
                variant="body2"
                color="#c62828"
                fontWeight="600"
                sx={{
                  fontSize: { xs: "10px", sm: "12px", md: "14px" },
                  mb: 0.5,
                }}
              >
                Run 1
              </Typography>
              <Typography
                variant="h4"
                fontWeight="bold"
                color="#b71c1c"
                sx={{
                  fontSize: { xs: "16px", sm: "20px", md: "24px" },
                  lineHeight: 1.1,
                  mb: 0.5,
                }}
              >
                {run1Value}
              </Typography>
              <Typography
                variant="caption"
                color="#d32f2f"
                sx={{
                  fontSize: { xs: "8px", sm: "10px", md: "11px" },
                  lineHeight: 1.1,
                }}
              >
                {run1Label}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={6}>
            <Paper
              sx={{
                p: { xs: 1.5, md: 2 },
                bgcolor: "#e8f5e8",
                border: "2px solid #c8e6c9",
                borderRadius: { xs: 1, md: 2 },
                textAlign: "center",
                height: { xs: "80px", md: "100px" }, // Fixed height
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <Typography
                variant="body2"
                color="#2e7d32"
                fontWeight="600"
                sx={{
                  fontSize: { xs: "10px", sm: "12px", md: "14px" },
                  mb: 0.5,
                }}
              >
                Run 2
              </Typography>
              <Typography
                variant="h4"
                fontWeight="bold"
                color="#1b5e20"
                sx={{
                  fontSize: { xs: "16px", sm: "20px", md: "24px" },
                  lineHeight: 1.1,
                  mb: 0.5,
                }}
              >
                {run2Value}
              </Typography>
              <Typography
                variant="caption"
                color="#388e3c"
                sx={{
                  fontSize: { xs: "8px", sm: "10px", md: "11px" },
                  lineHeight: 1.1,
                }}
              >
                {run2Label}
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        <Box textAlign="center">
          <Chip
            label={`${improvement ? "↗" : "↘"} ${difference}`}
            color={improvement ? "success" : "error"}
            variant="filled"
            sx={{
              fontWeight: 600,
              fontSize: { xs: "10px", sm: "12px", md: "13px" },
              height: { xs: 24, md: 28 },
            }}
          />
        </Box>
      </CardContent>
    </StyledCard>
  );
};

// ===== Transform raw to array format =====
function transformRun1(run1) {
  return [
    {
      "Metric Name": "Vulnerability Count",
      "Metric Value": run1.vulnerabilityCount,
    },
    {
      "Metric Name": "Vulnerability Density",
      "Metric Value": run1.vulnerabilityDensity,
    },
    { "Metric Name": "ROI", "Metric Value": run1.ROI },
    {
      "Metric Name": "Average Risk Rating",
      "Metric Value": run1.overallRiskRating,
    },
    {
      "Metric Name": "Vulnerability Distribution",
      "Metric Value": run1.vulnerabilityDistribution,
    },
    {
      "Metric Name": "False Positive Rate (%)",
      "Metric Value": run1.falsePositiveRate,
    },
    {
      "Metric Name": "True Positive Rate (%)",
      "Metric Value": run1.truePositiveRate,
    },
    {
      "Metric Name": "Business Impact Score",
      "Metric Value": run1.businessImpactScore,
    },
    {
      "Metric Name": "Technical Impact Score",
      "Metric Value": run1.technicalImpactScore,
    },
    {
      "Metric Name": "Run-1 Compliance Status",
      Value: [{ Standard: "OWASP", Status: run1.complianceMeet }],
    },
  ];
}

function transformRun2(run2) {
  return [
    {
      "Metric Name": "Vulnerability Count",
      "Metric Value": run2.vulnerabilitiesCount,
    },
    {
      "Metric Name": "Vulnerability Density",
      "Metric Value": run2.vulnerabilityDensity,
    },
    { "Metric Name": "ROI from R2", "Metric Value": run2.ROI },
    {
      "Metric Name": "Overall Risk Rating",
      "Metric Value": run2.overallRiskRating,
    },
    {
      "Metric Name": "Business Impact Score",
      "Metric Value": run2.businessImpactScore,
    },
    {
      "Metric Name": "Technical Impact Score",
      "Metric Value": run2.technicalImpactScore,
    },
    {
      "Metric Name": "Overall Security Posture (%)",
      "Metric Value": run2.securityPosture,
    },
    {
      "Metric Name": "Vulnerability Distribution (R2)",
      "Metric Value": run2.openVulnerabilityDistribution,
    },
    {
      "Metric Name": "Closed Vulnerabilities by Severity",
      "Metric Value": run2.closedVulnerabilityDistribution,
    },
    {
      "Metric Name": "Age of Vulnerabilities (Days)",
      "Metric Value": run2.averageVulnerabilityAge,
    },
    {
      "Metric Name": "Average Time to Remediate",
      "Metric Value": run2.averageRemediateDays,
    },
    {
      "Metric Name": "New Vulnerabilities in R2",
      Value: run2.newVulnerabilityDistribution,
    },
    {
      "Metric Name": "Run-2 Compliance Status",
      Value: [{ Standard: "OWASP", Status: run2.complianceMeet }],
    },
  ];
}

const ComparisonMetrics = ({ run1Metrics, run2Metrics }) => {
  // Your existing data transformation logic remains the same
  const run1 = transformRun1(run1Metrics || {});
  const run2 = transformRun2(run2Metrics || {});

  console.log("run1Metrics", run1Metrics);
  console.log("run2Metrics", run2Metrics);

  const run1OWASPData = transformOWASPData(run1Metrics.OWASPMetrics);
  // const run1TotalVulnerabilities = run1Metrics.vulnerabilityCount;

  const run2OWASPData = transformOWASPData(run2Metrics.owaspResponse);
  // const run2TotalVulnerabilities = run2Metrics.vulnerabilitiesCount;

  const findVal = (arr, name) =>
    arr.find((m) => m["Metric Name"] === name)?.["Metric Value"] ?? 0;

  const complianceList =
    run1.find((m) => m["Metric Name"] === "Run-1 Compliance Status")?.Value ||
    [];
  const complianceList2 =
    run2.find((m) => m["Metric Name"] === "Run-2 Compliance Status")?.Value ||
    [];

  const run1Data = run2Metrics.openVulnerabilityDistribution;
  const run2New = run2Metrics.newVulnerabilityDistribution;
  const run2NotFixed = run2Metrics.openVulnerabilityDistribution;
  console.log("run1Data", run1Data);
  console.log("run2New", run2New);

  // Your specified color codes
  const severityColors = {
    Critical: "#c00000",
    High: "#ff0000",
    Medium: "#ffc000",
    Low: "#39b549",
  };

  // Prepare data for stacked/grouped view
  const chartData = [
    {
      name: "Critical",
      "Run 1 Open": run1Data.Critical,
      "Run 2 New": run2New.Critical,
    },
    { name: "High", "Run 1 Open": run1Data.High, "Run 2 New": run2New.High },
    {
      name: "Medium",
      "Run 1 Open": run1Data.Medium,
      "Run 2 New": run2New.Medium,
    },
    { name: "Low", "Run 1 Open": run1Data.Low, "Run 2 New": run2New.Low },
  ];

  const totalRun1 = Object.values(run1Data).reduce((sum, val) => sum + val, 0);
  const totalRun2 = Object.values(run2New).reduce((sum, val) => sum + val, 0);

  const comparisonMetrics = [
    {
      title: "Vulnerability Count",
      run1Value: `${run1Metrics.vulnerabilityCount}`,
      run2Value: `${run2Metrics.vulnerabilitiesCount}`,
      run1Label: "Total Vulnerabilities",
      run2Label: "Total Vulnerabilities",
      difference: `- ${
        run1Metrics.vulnerabilityCount - run2Metrics.vulnerabilitiesCount
      }`,
      improvement: true,
      icon: Warning,
      tooltipText: tooltipTexts["Vulnerability Count"],
    },
    {
      title: "Vulnerability Density",
      run1Value: `${run1Metrics.vulnerabilityDensity}`,
      run2Value: `${run2Metrics.vulnerabilityDensity}`,
      run1Label: "per 1,000 LoC",
      run2Label: "per 1,000 LoC",
      difference: `- ${(
        run1Metrics.vulnerabilityDensity - run2Metrics.vulnerabilityDensity
      ).toFixed(2)}`,
      improvement: true,
      icon: GpsFixed,
      tooltipText: tooltipTexts["Vulnerability Density"],
    },
    {
      title: "Business Impact",
      run1Value: `${run1Metrics.businessImpactScore.toFixed(1)}`,
      run2Value: `${run2Metrics.businessImpactScore.toFixed(1)}`,
      run1Label: "Business Risk Score",
      run2Label: "Business Risk Score",
      difference: `- ${(
        run1Metrics.businessImpactScore - run2Metrics.businessImpactScore
      ).toFixed(1)}`,
      improvement: true,
      icon: People,
      tooltipText: tooltipTexts["Business Impact Score"],
    },
    {
      title: "Technical Impact",
      run1Value: `${run1Metrics.technicalImpactScore.toFixed(1)}`,
      run2Value: `${run2Metrics.technicalImpactScore.toFixed(1)}`,
      run1Label: "Technical Risk Score",
      run2Label: "Technical Risk Score",
      difference: `${(
        run1Metrics.technicalImpactScore - run2Metrics.technicalImpactScore
      ).toFixed(1)}`,
      improvement: true,
      icon: FlashOn,
      tooltipText: tooltipTexts["Technical Impact Score"],
    },
    {
      title: "Return on Investment (ROI)",
      run1Value: `${run1Metrics.ROI}`,
      run2Value: `${run2Metrics.ROI}`,
      run1Label: "ROI Multiplier",
      run2Label: "ROI Multiplier",
      difference: `${run1Metrics.ROI - run2Metrics.ROI}`,
      improvement: true,
      icon: AttachMoney,
      tooltipText: tooltipTexts["ROI"],
    },
    {
      title: "Overall Risk Rating",
      run1Value: `${run1Metrics.overallRiskRating.toFixed(2)}`,
      run2Value: `${run2Metrics.overallRiskRating.toFixed(2)}`,
      run1Label: "Risk Rating Score",
      run2Label: "Risk Rating Score",
      difference: `${
        run1Metrics.overallRiskRating.toFixed(2) -
        run2Metrics.overallRiskRating.toFixed(2)
      }`,
      improvement: true,
      icon: Warning,
      tooltipText: tooltipTexts["Overall Risk Rating"],
    },
  ];

  const run1Vulnerabilities = Object.entries(
    findVal(run1, "Vulnerability Distribution") || {}
  ).map(([label, value]) => ({
    label,
    value,
    color: {
      Critical: "#d32f2f",
      High: "#f57c00",
      Medium: "#fbc02d",
      Low: "#689f38",
    }[label],
  }));

  const run2Vulnerabilities = Object.entries(
    findVal(run2, "Vulnerability Distribution (R2)") || {}
  ).map(([label, value]) => ({
    label,
    value,
    color: {
      Critical: "#d32f2f",
      High: "#f57c00",
      Medium: "#fbc02d",
      Low: "#689f38",
    }[label],
  }));

  const closedVulnerabilities = Object.entries(
    findVal(run2, "Closed Vulnerabilities by Severity") || {}
  ).map(([label, value]) => ({
    label,
    value,
    color: {
      Critical: "#d32f2f",
      High: "#f57c00",
      Medium: "#fbc02d",
      Low: "#689f38",
    }[label],
  }));

  const vulnerabilityData = Object.entries(
    run2.find((m) => m["Metric Name"] === "New Vulnerabilities in R2")?.Value ||
      {}
  ).map(([label, count]) => ({
    label,
    count,
    color: {
      Critical: "#c00000",
      High: "#ff0000",
      Medium: "#ffc000",
      Low: "#39b549",
    }[label],
  }));

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        backgroundColor: "#FFFFFF",
        minHeight: "100vh",
      }}
    >
      {/* Header Section */}
      <Box mb={{ xs: 3, md: 4 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "flex-start", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 1.5, sm: 2 },
            mb: { xs: 2, md: 3 },
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: { xs: "24px", sm: "28px", md: "34px" },
              color: "#000000",
              lineHeight: { xs: 1.2, md: 1.1 },
            }}
          >
            Comparison Metrics
          </Typography>
          {/* <Button
            sx={{
              backgroundColor: "#0148A2",
              fontSize: { xs: "12px", sm: "14px" },
              fontWeight: 500,
              textTransform: "none",
              borderRadius: "8px",
              padding: { xs: "8px 16px", md: "0 12px" },
              width: { xs: "auto", md: "172px" },
              height: { xs: "32px", md: "40px" },
              color: "#FFFFFF",
              alignSelf: { xs: "flex-start", sm: "center" },
            }}
          >
            Comparison Analysis
          </Button> */}
        </Box>

        <Grid container spacing={{ xs: 2, md: 3 }}>
          {comparisonMetrics.map((metric, index) => (
            <Grid item xs={12} md={6} key={index}>
              <RunComparisonCard {...metric} />
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Main Metrics Cards */}
      <Grid container spacing={{ xs: 2, md: 3 }} mb={{ xs: 3, md: 4 }}>
        {[
          {
            title: "Overall Security Posture",
            value: `${findVal(run2, "Overall Security Posture (%)")}/10`,
            subtitle: getSecurityPostureDescription(
              findVal(run2, "Overall Security Posture (%)")
            ),
            trend: "up",
            trendValue: "+46.9%",
            color: "green",
            tooltipText: tooltipTexts["Overall Security Posture"],
          },
          {
            title: "Average Time to Remediation",
            value: `${findVal(run2, "Average Time to Remediate")} days`,
            subtitle: "Average Fix Time",
            trend: "down",
            trendValue: "-5.2 days",
            color: "blue",
            tooltipText: tooltipTexts["Average Time to Remediation"],
          },
          {
            title: "Age of Vulnerability",
            value: `${findVal(run2, "Age of Vulnerabilities (Days)")} days`,
            subtitle: "Average Days Unresolved",
            color: "orange",
            tooltipText: tooltipTexts["Age of Vulnerability"],
          },
        ].map((metric, idx) => (
          <Grid item xs={12} sm={6} lg={4} key={idx}>
            <MetricCard
              color={metric.color}
              sx={{
                height: { xs: "160px", md: "200px" },
                borderRadius: 2,
                border: "1px solid #5391F7",
                position: "relative", // Added for tooltip positioning
              }}
            >
              {/* Info Icon with Tooltip */}
              <Tooltip
                title={metric.tooltipText}
                placement="top"
                arrow
                componentsProps={{
                  tooltip: {
                    sx: {
                      maxWidth: 300, // Limit tooltip width for better readability
                      fontSize: "14px",
                      lineHeight: 1.4,
                    },
                  },
                }}
              >
                <IconButton
                  size="small"
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    padding: "4px",
                    color: "#5391F7",
                    "&:hover": {
                      backgroundColor: "rgba(83, 145, 247, 0.1)",
                    },
                  }}
                  aria-label="Info"
                >
                  <InfoIcon fontSize="small" sx={{ color: "grey.500" }} />
                </IconButton>
              </Tooltip>

              <CardContent
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  sx={{ flex: 1 }}
                >
                  <Box flex={1}>
                    <Typography
                      variant="body2"
                      sx={{
                        opacity: 0.9,
                        mb: 1,
                        fontSize: { xs: "12px", md: "14px" },
                      }}
                    >
                      {metric.title}
                    </Typography>
                    <Typography
                      variant="h4"
                      fontWeight="bold"
                      gutterBottom
                      sx={{ fontSize: { xs: "20px", sm: "24px", md: "32px" } }}
                    >
                      {metric.value}
                    </Typography>
                    {metric.subtitle && (
                      <Typography
                        variant="caption"
                        sx={{
                          opacity: 0.8,
                          fontSize: { xs: "10px", md: "12px" },
                        }}
                      >
                        {metric.subtitle}
                      </Typography>
                    )}
                    {metric.trend && metric.trendValue && (
                      <Box mt={1}>
                        <Chip
                          label={`${metric.trend === "up" ? "↗" : "↘"} ${
                            metric.trendValue
                          }`}
                          size="small"
                          sx={{
                            bgcolor: "rgba(255,255,255,0.2)",
                            color: "white",
                            fontWeight: 600,
                            fontSize: { xs: "10px", md: "12px" },
                            height: { xs: 20, md: 24 },
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                  {/* Original icon section removed */}
                </Box>
              </CardContent>
            </MetricCard>
          </Grid>
        ))}
      </Grid>

      {/* Vulnerability Distribution Charts */}
      <Grid container spacing={{ xs: 2, md: 3 }} mb={{ xs: 3, md: 4 }}>
        <Grid item xs={12} lg={6}>
          <DonutChart
            data={run1Vulnerabilities}
            title="Run 1 - Vulnerability Distribution by Severity"
            size={240}
            tooltipText={tooltipTexts["Vulnerability Distribution by Severity"]}
          />
        </Grid>
        <Grid item xs={12} lg={6}>
          <DonutChart
            data={run2Vulnerabilities}
            title="Run 2 - Open Vulnerabilities by Severity"
            size={240}
            tooltipText={tooltipTexts["Vulnerability Distribution by Severity"]}
          />
        </Grid>
      </Grid>

      {/* Closed Vulnerabilities and New Issues */}
      <Grid container spacing={{ xs: 2, md: 3 }} mb={{ xs: 3, md: 4 }}>
        <Grid item xs={12} lg={6}>
          <BarChart
            data={closedVulnerabilities}
            title="Closed Vulnerabilities by Severity"
            tooltipText={tooltipTexts["Closed Vulnerabilities by Severity"]}
          />
        </Grid>
        <Grid item xs={12} lg={6}>
          <StyledCard
            sx={{
              borderRadius: 2,
              border: "1px solid #5391F7",
              height: "100%",
              position: "relative", // Added for tooltip positioning
            }}
          >
            {/* Info Icon with Tooltip */}
            <Tooltip
              title={tooltipTexts["New Vulnerabilities"]}
              placement="top"
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    maxWidth: 300,
                    fontSize: "14px",
                    lineHeight: 1.4,
                  },
                },
              }}
            >
              <IconButton
                size="small"
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  padding: "4px",
                  color: "#5391F7",
                  "&:hover": {
                    backgroundColor: "rgba(83, 145, 247, 0.1)",
                  },
                  zIndex: 10,
                }}
                aria-label="Info"
              >
                <InfoIcon fontSize="small" sx={{ color: "grey.500" }} />
              </IconButton>
            </Tooltip>

            <CardContent
              sx={{ height: "100%", display: "flex", flexDirection: "column" }}
            >
              <Typography
                variant="h6"
                gutterBottom
                fontWeight="600"
                sx={{
                  fontSize: { xs: "16px", sm: "18px", md: "20px" },
                  mb: { xs: 1, md: 2 },
                }}
              >
                New Vulnerabilities in Run 2
              </Typography>
              <Stack
                spacing={{ xs: 1.5, md: 2 }}
                sx={{ flex: 1, justifyContent: "center" }}
              >
                {vulnerabilityData.map((item, index) => (
                  <Box
                    key={index}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box
                        width={{ xs: 12, md: 16 }}
                        height={{ xs: 12, md: 16 }}
                        borderRadius="50%"
                        bgcolor={item.color}
                      />
                      <Typography
                        variant="body2"
                        fontWeight="500"
                        sx={{ fontSize: { xs: "12px", sm: "14px" } }}
                      >
                        {item.label}
                      </Typography>
                    </Box>
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      sx={{ fontSize: { xs: "16px", sm: "18px", md: "20px" } }}
                    >
                      {item.count}
                    </Typography>
                  </Box>
                ))}

                <Divider />

                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography
                    variant="body2"
                    fontWeight="600"
                    sx={{ fontSize: { xs: "12px", sm: "14px" } }}
                  >
                    Total New
                  </Typography>
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    sx={{ fontSize: { xs: "16px", sm: "18px", md: "20px" } }}
                  >
                    {vulnerabilityData.reduce(
                      (acc, item) => acc + item.count,
                      0
                    )}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </StyledCard>
        </Grid>
      </Grid>

      <Grid container spacing={{ xs: 2, md: 3 }} mb={{ xs: 3, md: 4 }}>
        {/* Run-1 Compliance Cards */}
        {complianceList.map((compliance, idx) => (
          <Grid item xs={12} md={6} key={`run1-${idx}`}>
            <ComplianceCard
              title={"Run-1 OWASP"}
              status={compliance.Status}
              tooltipText={tooltipTexts["Compliance Requirement Met"]}
            />
          </Grid>
        ))}

        {/* Run-2 Compliance Cards */}
        {complianceList2.map((c, idx) => (
          <Grid item xs={12} md={6} key={`run2-${idx}`}>
            <ComplianceCard
              title={"Run-2 OWASP"}
              status={c.Status}
              tooltipText={tooltipTexts["Compliance Requirement Met"]}
            />
          </Grid>
        ))}
      </Grid>

      <Paper
        sx={{
          p: 3,
          border: "1px solid #5391F7",
          borderRadius: 2,
          position: "relative", // ← Add this line
          mb: 4,
        }}
      >
        <Box>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <WarningIcon sx={{ color: "#d32f2f", mr: 1 }} />
              <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                Vulnerability Analysis (R1 vs R2)
              </Typography>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Total: {totalRun1} open vulnerabilities from Run 1, {totalRun2}{" "}
              new vulnerabilities identified in Run 2
            </Typography>

            <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
              {Object.entries(severityColors).map(([severity, color]) => (
                <Box
                  key={severity}
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      backgroundColor: color,
                      mr: 1,
                      borderRadius: "2px",
                    }}
                  />
                  <Typography variant="body2">{severity}</Typography>
                </Box>
              ))}
            </Box>

            <Box sx={{ height: 300, width: "100%" }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={chartData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    domain={[0, "dataMax + 1"]}
                  />

                  <Bar dataKey="Run 1 Open" barSize={40}>
                    {chartData.map((entry, index) => (
                      <Cell
                        key={`run1-${index}`}
                        fill={severityColors[entry.name]}
                      />
                    ))}
                  </Bar>

                  <Bar dataKey="Run 2 New" barSize={40}>
                    {chartData.map((entry, index) => (
                      <Cell
                        key={`run2-${index}`}
                        fill={severityColors[entry.name]}
                        fillOpacity={0.7} // Slightly transparent to differentiate
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>

            <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
              {/* Dynamic chips for each severity level with not fixed count > 0 */}
              {Object.entries(run2NotFixed)
                .filter(([severity, count]) => count > 0)
                .map(([severity, count]) => (
                  <Chip
                    key={severity}
                    label={`${count} ${severity.toLowerCase()} not fixed`}
                    size="small"
                    variant="outlined"
                    sx={{
                      borderColor: severityColors[severity] || "#666",
                      color: severityColors[severity] || "#666",
                      fontWeight: 500,
                    }}
                  />
                ))}

              {/* Open vulnerabilities chip - always show */}
              <Chip
                label={`${totalRun1} open vulnerabilities`}
                size="small"
                variant={totalRun1 > 0 ? "filled" : "outlined"}
                sx={{
                  backgroundColor: totalRun1 > 0 ? "#2196f3" : "transparent",
                  borderColor: totalRun1 === 0 ? "#4caf50" : "#2196f3",
                  color: totalRun1 > 0 ? "white" : "#4caf50",
                  fontWeight: 500,
                  "&:hover": {
                    backgroundColor:
                      totalRun1 > 0 ? "#1976d2" : "rgba(76, 175, 80, 0.1)",
                  },
                }}
              />

              {/* New vulnerabilities chip - always show */}
              <Chip
                label={`${totalRun2} new vulnerabilities`}
                size="small"
                variant={totalRun2 > 0 ? "filled" : "outlined"}
                sx={{
                  backgroundColor: totalRun2 > 0 ? "#ff9800" : "transparent",
                  borderColor: totalRun2 === 0 ? "#4caf50" : "#ff9800",
                  color: totalRun2 > 0 ? "white" : "#4caf50",
                  fontWeight: 500,
                  "&:hover": {
                    backgroundColor:
                      totalRun2 > 0 ? "#f57c00" : "rgba(76, 175, 80, 0.1)",
                  },
                }}
              />

              {/* Special message when both are 0 */}
              {totalRun2 === 0 && totalRun1 === 0 && (
                <Chip
                  label="Perfect security posture!"
                  size="small"
                  color="success"
                  variant="filled"
                  sx={{ fontWeight: 500 }}
                />
              )}
            </Box>
          </CardContent>
        </Box>
      </Paper>

      {/* OWASP Top 10 Comparison */}
      <Paper
        sx={{
          p: 3,
          border: "1px solid #5391F7",
          borderRadius: 2,
          position: "relative", // ← Add this line
        }}
      >
        <Typography sx={{ fontSize: "23px", fontWeight: 400 }} gutterBottom>
          Vulnerability Distribution by OWASP Top 10
        </Typography>

        <Tooltip
          title={tooltipTexts["OWASP Top 10"]}
          placement="top"
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                maxWidth: 300,
                fontSize: "14px",
                lineHeight: 1.4,
              },
            },
          }}
        >
          <IconButton
            size="small"
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              padding: "4px",
              color: "#5391F7",
              "&:hover": {
                backgroundColor: "rgba(83, 145, 247, 0.1)",
              },
            }}
            aria-label="Info"
          >
            <InfoIcon fontSize="small" sx={{ color: "grey.500" }} />
          </IconButton>
        </Tooltip>

        <OwaspComparisonChart
          run1Data={run1OWASPData}
          run2Data={run2OWASPData}
        />
      </Paper>
    </Box>
  );
};

export default ComparisonMetrics;
